import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { JSX, ReactNode } from "react";
import { useApiClient } from "../api/ApiContext";
import { queryKeys } from "../api/queryKeys";
import { myTenantsSchema } from "../api/schemas";
import { useAuth } from "../shared/auth/AuthContext";
import { useI18n } from "../shared/i18n/I18nContext";
import { useSelectedTenant, useTenantSelectionStore } from "../shared/session/tenantSelection";
import { Loading } from "../shared/ui/Loading";
import { PublicLayout } from "../shared/ui/PublicLayout";
import layoutStyles from "../shared/ui/PublicLayout.module.css";
import styles from "../shared/ui/ui.module.css";

/**
 * Loads the employers of the signed-in person (`GET /me/tenants`).
 *
 * @returns The query.
 */
export function useMyTenants(): ReturnType<typeof useQuery<{ tenantId: string; name: string }[]>> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.myTenants,
    queryFn: ({ signal }) => client.get("/api/v1/me/tenants", myTenantsSchema, { signal }),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  return query;
}

/** Properties of {@link TenantGate}. */
export interface TenantGateProps {
  /** The application, shown once the tenant is clear. */
  readonly children: ReactNode;
}

/**
 * Asks people with several employers which tenant they work in, once per session (T-105,
 * spec 18.3 no. 5); nothing is guessed. People with one employer never see the question. Without
 * a connection the application continues with the last choice. The question is a public screen
 * (UX-120): the employers are equal full-width choices, and „Abmelden“ ends the session instead.
 *
 * @param props - Component properties.
 * @returns The selection or the application.
 */
export function TenantGate(props: TenantGateProps): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const store = useTenantSelectionStore();
  const selected = useSelectedTenant();
  const queryClient = useQueryClient();
  const tenants = useMyTenants();
  const list = tenants.data ?? [];
  const selectionValid = selected !== null && list.some((tenant) => tenant.tenantId === selected);

  // A choice that no longer applies (one employer left, tenant removed) is forgotten.
  useEffect(() => {
    if (user !== null && tenants.data !== undefined && selected !== null && !selectionValid) {
      store.clear(user.subject);
    }
  }, [user, tenants.data, selected, selectionValid, store]);

  // Only the first load while online waits; offline (query paused) or after a failure the
  // application continues, and a refetch never unmounts it again.
  const firstLoad = tenants.isLoading && tenants.errorUpdateCount === 0;

  if (firstLoad && selected === null) {
    const loading = <Loading />;
    return loading;
  }

  if (user === null || list.length <= 1 || selectionValid) {
    const application = <>{props.children}</>;
    return application;
  }

  const choose = (tenantId: string): void => {
    store.set(user.subject, tenantId);
    void queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] !== queryKeys.myTenants[0],
    });
  };
  const selection = (
    <PublicLayout title={t("tenantSelection.title")} look="signIn">
      <p>{t("tenantSelection.intro")}</p>
      <ul className={styles["stack"]} aria-label={t("tenantSelection.title")}>
        {list.map((tenant) => (
          <li key={tenant.tenantId}>
            <button
              type="button"
              className={layoutStyles["tenantChoice"]}
              onClick={() => {
                choose(tenant.tenantId);
              }}
            >
              <span className={layoutStyles["tenantTile"]} aria-hidden="true">
                {tenant.name.trim().charAt(0).toUpperCase()}
              </span>
              {tenant.name}
              <svg
                className={layoutStyles["tenantChevron"]}
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </li>
        ))}
      </ul>
    </PublicLayout>
  );

  return selection;
}
